import { useState, type FC, type ReactNode } from "react";
import { Box } from "@mui/material";
import { Translate, useTranslation } from "@privacy/translations";
import { useForm } from "react-hook-form";
import { PencilIcon, MeasureIcon2 } from "../../../shared/icons";
import type { DrawerHeaderProps } from "../../../shared/Drawer";
import { Drawer } from "../../../shared/Drawer";
import * as FormDialog from "../../../shared/FormDialog";
import { deleteMeasureMutationOptions, updateMeasureMutationOptions } from "../../../api/measures";
import type { MeasureById } from "../../../api/measures.types";
import type { TranslationKey } from "@privacy/translations";
import { usePermission } from "../../../shared/permissions";
import { usePrivacyNavigate } from "../../../shared/usePrivacyNavigate";
import { ActionMenu, ActionMenuDeleteMenuItem, ActionMenuItemWithPermission } from "../../../shared/ActionMenu";
import { useMutation } from "@tanstack/react-query";

export const MeasuresDrawerHeader: FC<{
  measure: MeasureById;
  statusIndicator: DrawerHeaderProps["status"];
  subDrawerHeader?: ReactNode;
}> = ({ measure, statusIndicator, subDrawerHeader }) => {
  const { navigate, paths } = usePrivacyNavigate();

  return (
    <>
      <Drawer.Header
        status={statusIndicator}
        title={measure.name}
        renderIcon={props => <MeasureIcon2 {...props} />}
        onClose={() => navigate(paths.measuresGrid())}
        topRightSlot={
          <MeasureActionMenu
            measureId={measure.id}
            measureName={measure.name}
            onDeleted={() => navigate(paths.measuresGrid())}
            ariaLabelKey="GridDrawer.Menu.ButtonLabel"
          />
        }
      />
      {subDrawerHeader}
    </>
  );
};

const MeasureEditDialog: FC<{ measureName: string; measureId: string; onClose: () => void }> = ({
  measureName,
  measureId,
  onClose,
}) => {
  const { t } = useTranslation();
  const { handleSubmit, control, setFocus } = useForm<{ name: string }>({
    defaultValues: { name: measureName },
  });

  const updateMeasureMutation = useMutation(updateMeasureMutationOptions());

  FormDialog.useAutoFocusField(setFocus, "name");

  const handleUpdateMeasure = (payload: { name: string }) => {
    updateMeasureMutation.mutate({ measureId: measureId, payload }, { onSuccess: () => onClose() });
  };

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("SystemsDrawer.Edit.DialogHeader")}</FormDialog.Title>

      <FormDialog.Form onSubmit={handleSubmit(handleUpdateMeasure)}>
        <FormDialog.Content>
          <FormDialog.FormError isError={updateMeasureMutation.isError} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="name"
              rules={{ required: t("CreateMeasure.Dialog.Validation.NameRequired") }}
              label={t("CreateMeasure.Dialog.NameLabel")}
              placeholder={t("CreateMeasure.Dialog.NamePlaceholder")}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={updateMeasureMutation.isPending}>
            {t("EditMeasure.Dialog.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};

export const MeasureActionMenu = ({
  measureId,
  measureName,
  onDeleted,
  ariaLabelKey,
}: {
  measureId: string;
  measureName: string;
  onDeleted: () => void;
  ariaLabelKey?: TranslationKey;
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { hasPermission } = usePermission();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const deleteMeasureMutation = useMutation(deleteMeasureMutationOptions());

  return (
    <>
      {isEditOpen && (
        <MeasureEditDialog onClose={() => setIsEditOpen(false)} measureName={measureName} measureId={measureId} />
      )}
      <ActionMenu isOpen={isOpen} onOpenChange={setIsOpen} ariaLabelKey={ariaLabelKey}>
        <ActionMenuItemWithPermission
          missingPermission={!hasPermission("nova-forms-api:measure:update")}
          onClick={() => {
            setIsEditOpen(true);
            setIsOpen(false);
          }}
        >
          <PencilIcon />
          {t("Measures.Drawer.Edit.Label")}
        </ActionMenuItemWithPermission>

        <ActionMenuDeleteMenuItem
          missingPermission={!hasPermission("nova-forms-api:measure:delete")}
          confirmDeleteDialog={{
            header: t("Measures.Delete.ConfirmHeader"),
            message: (
              <Translate
                translationKey="Measures.Delete.ConfirmMessage"
                values={{
                  name: (
                    <Box component="span" sx={{ fontWeight: "bold" }}>
                      {measureName}
                    </Box>
                  ),
                }}
              />
            ),
            delete: t("Measures.Delete.ConfirmDelete"),
          }}
          onClose={() => setIsOpen(false)}
          onDelete={() => deleteMeasureMutation.mutateAsync(measureId, { onSuccess: () => onDeleted() })}
        />
      </ActionMenu>
    </>
  );
};
